import translate from '../../core/tools/translate';
import worldMap from '../../core/map';
import CoreControl from '../CoreControl';
import { VectorType } from '../VectorType';
import filters from '../../core/filters';
import _, { isNaN } from "lodash";
import moment from 'moment';
import environment from '../../environment';
import { pinBuilder } from '../../core/map/pinBuilder';
import store from '../../../store';
import {
    OVERVIEW_PANEL_CHANGE_CURRENT,
    OVERVIEW_PANEL_IS_VISIBLE
} from '../../../store/modules/overview-panel/types';
import databoard from '../../core/databoard';
import DataTable from '../../core/charts/Datatable';
import templates from '../../core/templates';
import { FeaturePermission } from '../../../constants/feature.permission.helper';
import api from '../../api';
import { numeralMixin } from '../../../modules/shared/mixins/numeral.mixin';

const LayerControls = (name) => require('../../controls').get(name);
const GetLayer = (name, currentMap = worldMap.getCurrent()) => {
    return require('../../layers/index').default.get(name, currentMap);
};

const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);

export class ClientSalesAfterSalesControl extends CoreControl {
    period;

    constructor() {
        super({ name: 'ClientSalesAfterSales' });

        this.createFilter = this.createFilter.bind(this);
        this._filterCallback = this._filterCallback.bind(this);
        this._change = this.change.bind(this);
        this._changePeriod = this._changePeriod.bind(this);
        this._getDate = this._getDate.bind(this);
        this.toggleOverview = this.toggleOverview.bind(this);

        this._instance = null;
        this._isRedraw = false;

        this._tags = {
            tagVmm: {
                name: 'tagVmm',
                property: 'Value',
                align: 'west',
                size: 11,
                bold: true,
                box: true,
                mask: '$ 0,0.00',
                offsetY: 50,
                offsetX: -15
            },
            tagVehicleCount: {
                name: 'tagVehicleCount',
                property: 'VehicleCount',
                align: 'west',
                size: 11,
                bold: true,
                box: true,
                mask: '0',
                offsetY: 25,
                offsetX: -15,
                colorize: true,
            },
            tagTicketAverage: {
                name: 'tagTicketAverage',
                property: 'TicketAverage',
                align: 'west',
                size: 11,
                bold: true,
                box: true,
                mask: '$ 0,0.00',
                offsetY: 0,
                offsetX: -15,
                color: 'green',
                colorize: true,
            }
        };

        this._clusterImage = new Image();
        this._clusterImage.src = `${environment.get('IMAGES_HOST')}/icon_marker_points.png`;
        this._salesFeatures = [];

        this._networkLayer = GetLayer('NetworkAfterSales');
        this._originalNetworkClusterTemplate = this._networkLayer.options.symbology.cluster.template;

        this._overviewFeatures = [];

        const imageNetwork = environment.get('CONFIG.CLIENT');
        this._popupImage = `${environment.get('IMAGES_HOST')}/aftersales/pin_${imageNetwork.toUnderscoreCase()}_xs.png`;
    }

    static getInstance() {
        if (!ClientSalesAfterSalesControl._instance) {
            ClientSalesAfterSalesControl._instance = new ClientSalesAfterSalesControl();
        }

        return ClientSalesAfterSalesControl._instance;
    }

    init() {
        const values = {
            help: translate(`Help.${this.options.name}`),
            id: 'layer-client-sales-after-sales',
            input: 'checkbox',
            label: translate(`Common.${this.options.name}`),
            layer: this.getLayer,
            name: this.options.name,
            type: 'vector-range',
            change: this._change,
            links: {},
            checkbox: {},
            overview: this.toggleOverview,
            filter: {
                enable: isAuthorized(FeaturePermission.LAYER_AFTER_SALES_CLIENT_SALES.filter),
                click: this.createFilter
            }
        };

        const {start, end} = this._buildInitPeriod();

        values.links[translate('Action.ShowDateFrom')] = {
            id: 'layer-client-sales-date',
            name: 'filterYear',
            text: `${moment(start).format('MMM/YYYY')} -> ${moment(end).format('MMM/YYYY')}`,
            value: {
                start: start.toDate(),
                end: end.toDate(),
                max: end.toDate(),
                min: start.toDate()
            },
            change: this._changePeriod,
            type: 'datepicker',
            dataSource: this.buildDataSourceDatepicker()
        };

        values.checkbox[translate('Action.ShowAverageSales')] = {
            name: 'tagVmm',
            displaySection: true,
            change: (isChecked) => this._toggleSales(isChecked, 'tagVmm'),
            style: {
                change: (tag) => {
                    this._tags.tagVmm = tag;
                    this._toggleLabel();
                },
                options: this._tags.tagVmm
            }
        };

        values.checkbox['Venda Itens'] = {
            name: 'tagVehicleCount',
            displaySection: true,
            change: (isChecked) => this._toggleSales(isChecked, 'tagVehicleCount'),
            style: {
                change: (tag) => {
                    this._tags.tagVehicleCount = tag;
                    this._toggleLabel();
                },
                options: this._tags.tagVehicleCount
            }
        };

        values.checkbox['Ticket Médio'] = {
            name: 'tagTicketAverage',
            displaySection: true,
            change: (isChecked) => this._toggleSales(isChecked, 'tagTicketAverage'),
            style: {
                change: (tag) => {
                    this._tags.tagTicketAverage = tag;
                    this._toggleLabel();
                },
                options: this._tags.tagTicketAverage
            }
        };

        if (!this._instance) {
            this._instance = new VectorType(values);
        }
    }

    createFilter() {
        if (!isAuthorized(FeaturePermission.LAYER_AFTER_SALES_CLIENT_SALES.filter)) {
            return;
        }

        filters.create({
            id: `${_.kebabCase(this.options.name)}-filter-id`,
            label: translate(`Common.${this.options.name}`),
            type: 'clientSalesAfterSales',
            onSubmit: this._filterCallback,
            fields: {
                salesType: true
            }
        });
    }

    buildDataSourceDatepicker() {
        if (isAuthorized(FeaturePermission.LAYER_AFTER_SALES_CLIENT_SALES.filterLast12Months)) {
            return [
                { text: 'Últimos 12 meses', value: 5 },
                { text: 'Anual', value: 0 },
                { text: 'Semestral', value: 1 },
                { text: 'Trimestral', value: 2 },
                { text: 'Bimestral', value: 3 },
                { text: 'Mensal', value: 4 },
                { text: 'Personalizado', value: -1 }
            ];
        }
    }

    _buildInitPeriod(id = this.getLayer().getMap()._id) {
        const datePrincipal = this._getDate();

        if (isAuthorized(FeaturePermission.LAYER_AFTER_SALES_CLIENT_SALES.filterLast12Months)) {
            this._date.set(id, {
                start: moment(datePrincipal.end.clone().subtract(11, 'months'),),
                end: moment(datePrincipal.end.clone())
            });
            return {
                start: datePrincipal.end.clone().subtract(11, 'months'),
                end: datePrincipal.end.clone()
            };
        }

        return {
            start: datePrincipal.start.clone(),
            end: datePrincipal.end.clone()
        };
    }

    change(e, completeRequest) {
        const layer = this.getLayer();
        const { params } = layer.options.remote;

        if (e.checked) {
            this._period = this._getDate();
            params.monthInitial = this._period.start.month() + 1;
            params.monthEnd = this._period.end.month() + 1;
            params.yearInitial = this._period.start.year();
            params.yearEnd = this._period.end.year();


            this.getLayer().setOptions({
                remote: {
                    params
                }
            });

            if (this.getControls().checkbox.tagVmm.status) {
                this._toggleSales(true, 'tagVmm');
            }

            if (this.getControls().checkbox.tagVehicleCount.status) {
                this._toggleSales(true, 'tagVehicleCount');
            }

            if (this.getControls().checkbox.tagTicketAverage.status) {
                this._toggleSales(true, 'tagTicketAverage');
            }

            setTimeout(() => {
                this.getControls().slider.range.input.value(5);
                this.getControls().slider.range.value = 5;
            }, 200);

            this.getControls().button.overview.input.enable(true);
        } else {
            this.getControls().button.overview.input.enable(false);
            filters.close();
            databoard.hide();
            delete this._overviewFeatures;
            this._toggleSales(false, 'tagVmm', false);
            this._toggleSales(false, 'tagVehicleCount', false);
            this._toggleSales(false, 'tagTicketAverage', false);
        }

        if (e.redraw || this._isRedraw) {
            this.getLayer()
                .redraw()
                .then(() => setTimeout(() => {
                    this._loadNetworks(e.checked);
                    if (completeRequest) {
                        completeRequest();
                    } else {
                        this._loadDataForTags();
                    }
                }, 500));
            this._isRedraw = false;
        } else {
            super._change(e, () => {
                setTimeout(() => {
                    this._loadNetworks(e.checked);
                    this._loadDataForTags();

                    if (e.checked) {
                        this.createFilter();
                    }

                    setTimeout(() => {
                        worldMap.getCurrent().fire('dragend');
                    }, 1000);
                }, 500);
            });
        }

        if (!this._isEventDragEnd) {
            worldMap.getCurrent().on('dragend', _.debounce(() => {
                this._loadDataForTags();
                this.updateOverview();
            }), 500);

            worldMap.getCurrent().on('zoomend', _.debounce(() => {
                this._loadDataForTags();
                this.updateOverview();
            }), 500);

            this._isEventDragEnd = true;
        }
    }

    updateOverview() {
        if (store.getters[OVERVIEW_PANEL_IS_VISIBLE] && this.getControls().switch.onOff.status) {
            const features = this._networkLayer.getCurrentFeatures();
            this.summary(features);
            this._overviewFeatures = _.cloneDeep(features);
        }
    }

    toggleOverview() {
        const features = this._networkLayer.getCurrentFeatures();

        if ((_.size(features) && !_.isEqual(this._overviewFeatures, features)) || !store.getters[OVERVIEW_PANEL_IS_VISIBLE]) {
            this.summary(features);
            this._overviewFeatures = _.cloneDeep(features);

            store.dispatch(OVERVIEW_PANEL_CHANGE_CURRENT, this.options.name);
        }
    }

    summary(features) {
        const filterData = filters.get(`${_.kebabCase(this.options.name)}-filter-id`);
        const salesTypes = filterData.salesTypes
            ? filterData.salesTypes.text.join(', ')
            : '';
        const data = this.calculate(features);
        const periodSelectedWithClient = `${environment.get('CONFIG.CLIENT')} | ${moment(this._getDate().start).format('MMM/YYYY')} - ${moment(this._getDate().end).format('MMM/YYYY')}`;

        const chartPie = new DataTable(data.salesTypeTotal, {
            row: 'name',
            column: ['total']
        });

        const chartColumn = new DataTable(data.rankingStoresSales, {
            row: 'name',
            column: [`Vendas (${salesTypes || 'Todos'})`],
            sort: 'name'
        });

        let dataSource = [];
        dataSource.push(data.summary);

        if (isAuthorized(FeaturePermission.LAYER_AFTER_SALES_CLIENT_SALES.filterSalesType)) {
            dataSource.push(chartPie);
        }

        dataSource.push(chartColumn);

        const options = {
            title: {
                table: `Resumo de Vendas ${periodSelectedWithClient}`,
                chartPie: isAuthorized(FeaturePermission.LAYER_AFTER_SALES_CLIENT_SALES.filterSalesType) ? `Vendas por Tipo (${salesTypes || 'Todos'})` : undefined,
                chartColumn: `Ranking de Vendas`,
            },
            chart: !isAuthorized(FeaturePermission.LAYER_AFTER_SALES_CLIENT_SALES.filterSalesType),
            chartSplitEqual: !isAuthorized(FeaturePermission.LAYER_AFTER_SALES_CLIENT_SALES.filterSalesType),
            dash: isAuthorized(FeaturePermission.LAYER_AFTER_SALES_CLIENT_SALES.filterSalesType),
            data: dataSource,
            convertTable: false,
            format: [
                null,
                '{0:c}',
                '{0:c}'
            ],
            limit: 20,
            label: true,
            columns: [
                {
                    field: 'city',
                    title: translate('Common.City')
                },
                {
                    field: 'state',
                    title: translate('Common.State'),
                },
                {
                    field: 'totalStores',
                    title: `Lojas`,
                },
                {
                    field: 'average',
                    title: 'Venda Média Mensal',
                    template: (data) => numeralMixin.methods.numeral(data.average).format('$ 0,0.00'),
                },
                {
                    field: 'total',
                    title: 'Venda Total',
                    template: (data) => numeralMixin.methods.numeral(data.total).format('$ 0,0.00'),
                },
            ],
            legends: {
                dash1: {
                    visible: true,
                    position: 'right',
                    labels: {
                        font: `10px 'Roboto Condensed', sans-serif`,
                        template: (dataItem) => {
                            const { text, value } = dataItem;
                            const val = numeralMixin.methods.numeral(value).format('$ 0,0.00');
                            return `${text} - ${val}`;
                        }
                    }
                }
            },
            tooltip: {
                pie: (dataItem) => {
                    const { category, value } = dataItem;
                    const val = numeralMixin.methods.numeral(value).format('$ 0,0.00');
                    return `${category} - ${val}`;
                },
                column: (dataItem) => {
                    const { series, value } = dataItem;
                    const val = numeralMixin.methods.numeral(value).format('$ 0,0.00');
                    return `${series.name} - ${val}`;
                }
            },
            buttons: {
                fullscreen: true,
                export: true
            }
        };

        databoard.show(options);
    }

    calculate(features) {
        const filterData = filters.get(`${_.kebabCase(this.options.name)}-filter-id`);
        const salesTypes = filterData.salesTypes
            ? filterData.salesTypes.text.join(', ')
            : '';

        const summary = _(features)
            .filter(f => f.properties)
            .map(f => f.properties)
            .flatten()
            .groupBy(f => `${f.City}, ${f.StateAcronym}`)
            .map((values) => {
                const crrValue = values.findOrFirst(x => true);
                return {
                    city: !!crrValue ? crrValue.City : '',
                    state: !!crrValue ? crrValue.StateAcronym : '',
                    totalStores: values.length,
                    total: _.sumBy(values, 'Total'),
                    average: this._calculateSalesAverage(values)
                };
            })
            .orderBy(f => f.total)
            .reverse()
            .map(f => f)
            .value();

        const salesTypeTotal = _(features)
            .filter(f => f.properties)
            .map(f => f.properties)
            .flatten()
            .map(f => f.SalesTypeAverages)
            .flatten()
            .groupBy(f => f.SalesTypeName)
            .map((values, key) => {
                return {
                    name: key,
                    total: Math.round(_.sumBy(values, 'Value') * 100) / 100,
                };
            })
            .orderBy(f => f.total)
            .map(f => f)
            .value();

        const rankingStoresSales = _(features)
            .filter(f => f.properties)
            .map(f => f.properties)
            .flatten()
            .map((value) => {
                return {
                    name: value.Name,
                    [`Vendas (${salesTypes || 'Todos'})`]: value.Total
                };
            })
            .orderBy(f => f[`Vendas (${salesTypes || 'Todos'})`])
            .reverse()
            .take(15)
            .value();

        return {
            summary,
            salesTypeTotal,
            rankingStoresSales
        };
    }

    _calculateSalesAverage(values) {
        const months = this._getMonths();
        const result = _.sumBy(values, 'Total') === 0
            ? 0
            : _.sumBy(values, 'Total') / months;

        return isNaN(result)
            ? 0
            : result;
    }

    _loadNetworks(checked) {
        const networkControls = LayerControls('NetworkAfterSales').getControls();
        const { symbology, popup } = this._networkLayer.options;

        if (!checked) {
            symbology.cluster.template = this._originalNetworkClusterTemplate;
            popup.clusterTemplate = null;
            this._networkLayer.setOptions({ symbology });
        } else {
            symbology.cluster.template = this._createClusterTemplate.bind(this);
            popup.clusterTemplate = this._createClusterPopupTemplate.bind(this);
            this._networkLayer.setOptions({ symbology });
            networkControls.switch.onOff.input.check(true);
        }
    }

    _createClusterPopupTemplate(features) {
        if (worldMap.getZoom() < 6) return;

        const totals = {
            totalStores: _.size(features),
            total: _.sumBy(features, 'feature.properties.Total'),
            totalAvgSalesInCluster: _.sumBy(features, 'feature.properties.Value'),
        };

        totals['totalAvgSales'] = numeralMixin.methods.numeral(_.divide(totals.totalAvgSalesInCluster, totals.totalStores)).format('($ 0,0.00 a)').replace(/milhões/, 'mi');
        totals['total'] = numeralMixin.methods.numeral(totals.total).format('($ 0,0.00 a)').replace(/milhões/, 'mi');
        totals['totalAvgSalesInCluster'] = numeralMixin.methods.numeral(totals.totalAvgSalesInCluster).format('($ 0,0.00 a)').replace(/milhões/, 'mi');

        const values = {
            period: {
                start: this._getDate().start.format('MM/YYYY'),
                end: this._getDate().end.format('MM/YYYY'),
            },
            image: this._popupImage,
            stores: _(features)
                .map(f => f.feature)
                .map(f => f.properties)
                .map(f => ({
                    name: f.Name.split('-')[1] ? f.Name.split('-')[1] : f.Name,
                    average: f.Value
                }))
                .orderBy(f => f.average)
                .reverse()
                .map(x => ({
                    name: x.name,
                    abreviation: x.name.length > 15 ? `${x.name.substring(0, 15)}...` : x.name,
                    average: numeralMixin.methods.numeral(x.average).format('($ 0,0.00 a)').replace(/milhões/, 'mi'),
                }))
                .value(),
            ...totals
        };

        return $(templates('map-layer-stores-cluster-after-sales-popup', values));
    };

    _createClusterTemplate(cluster) {
        const f = LayerControls('NetworkAfterSales');
        const showSales = this.getControls().switch.onOff;
        let data = {};

        if ((showSales || {}).status) {
            const months = this._getMonths();
            const features = cluster.getAllChildMarkers();
            features.forEach((f) => {
                if (this._salesFeatures[f.feature.properties.Id]) {
                    f.feature.properties.Total = this._salesFeatures[f.feature.properties.Id].Value;
                    f.feature.properties.Value = _.divide(this._salesFeatures[f.feature.properties.Id].Value, months);
                    f.feature.properties.VehicleCount = Math.round(_.divide(this._salesFeatures[f.feature.properties.Id].VehicleCount, months));
                    f.feature.properties.TicketAverage = f.feature.properties.VehicleCount === 0
                        ? 0
                        : _.divide(f.feature.properties.Value, f.feature.properties.VehicleCount);
                    f.feature.properties.SalesTypeAverages = this._salesFeatures[f.feature.properties.Id].SalesTypeAverages;
                    return;
                }

                f.feature.properties.Total = 0;
                f.feature.properties.Value = 0;
                f.feature.properties.VehicleCount = 0;
                f.feature.properties.TicketAverage = 0;
                f.feature.properties.SalesTypeAverages = [];
            });

            data = {
                value: Math.round(_.sumBy(features, 'feature.properties.Value')) || 0,
                isCurrency: true
            };
        } else {
            data = {
                value: cluster.getChildCount()
            };
        }

        return pinBuilder.create.pin.imageWithValue(data, this._clusterImage);
    }

    _getDate(id = this.getLayer().getMap()._id) {
        const period = environment.get('CONFIG.FILTERS.SALES_PERIOD');

        if (_.isUndefined(this._date)) {
            this._date = new Map();
        }

        if (!this._date.has(id)) {

            if (id === 0)
                this._date.set(id, { start: moment(period.periodInitialFormatted, "YYYY-MM-DD"), end: moment(period.periodEndFormatted, "YYYY-MM-DD") });
            else
                this._date.set(id, this._date.get((id - 1)));
        }

        return this._date.get(id);
    }

    _filterCallback(filterData) {
        const layer = this.getLayer();
        const { params } = layer.options.remote;

        params.salesTypeIds = null;

        if (filterData.salesTypes) {
            params.salesTypeIds = _.isArray(filterData.salesTypes.value)
                ? filterData.salesTypes.value
                : [filterData.salesTypes.value];
        }

        layer.setOptions({
            remote: {
                params
            }
        });

        layer
            .redraw()
            .then(() => {
                if (this.getControls().switch.onOff.status) {
                    setTimeout(() => {
                        this._toggleSales(this.getControls().checkbox.tagVmm.status, 'tagVmm', false);
                        this._toggleSales(this.getControls().checkbox.tagVehicleCount.status, 'tagVehicleCount', false);

                        setTimeout(() => {
                            worldMap.getCurrent().fire('dragend');
                        }, 1000);
                    }, 500);
                }
            });
    }

    _changePeriod(date, id = this.getLayer().getMap()._id) {
        this._date.set(id, {
            start: moment(date.start),
            end: moment(date.end)
        });

        if (this.getControls().switch.onOff.status) {
            this.change({ checked: this.getLayer().visible(), redraw: true }, () => {
                if (!this.getControls().checkbox.tagVmm.status && !this.getControls().checkbox.tagVehicleCount.status) {
                    this._loadDataForTags();
                }
                this._toggleSales(this.getControls().checkbox.tagVmm.status, 'tagVmm');
                this._toggleSales(this.getControls().checkbox.tagVehicleCount.status, 'tagVehicleCount');
                this.updateOverview();
            });
        } else {
            this._isRedraw = true;
        }
    }

    _getLayerTag() {
        let tags = this._networkLayer.options.tag || [];

        if (this.getControls().checkbox.tagVmm.status) {
            tags = [this._tags.tagVmm, ...tags];
        }

        if (this.getControls().checkbox.tagVehicleCount.status) {
            tags = [this._tags.tagVehicleCount, ...tags];
        }

        if (this.getControls().checkbox.tagTicketAverage.status) {
            tags = [this._tags.tagTicketAverage, ...tags];
        }

        tags = _.uniqBy(tags, 'name');

        return tags;
    }

    _getMonths() {
        const { end, start } = this._getDate();
        const monthDiff = (d1, d2) => {
            let months = (d2.year() - d1.year()) * 12;
            months -= d1.month() + 1;
            months += d2.month() + 2;
            return months > 0 ? months : 1;
        };

        return monthDiff(start, end);
    }

    _loadDataForTags() {
        const months = this._getMonths();
        const networksFeatures = this._networkLayer.toGeoJSON();
        this._salesFeatures = this.getLayer().getFeatures().reduce((acc, feature) => {
            acc[feature.properties.StoreId] = feature.properties;
            return acc;
        }, {});

        networksFeatures.features.forEach((f) => {
            if (this._salesFeatures[f.properties.Id]) {
                f.properties.Total = this._salesFeatures[f.properties.Id].Value;
                f.properties.Value = _.divide(this._salesFeatures[f.properties.Id].Value, months);
                f.properties.VehicleCount = Math.round(_.divide(this._salesFeatures[f.properties.Id].VehicleCount, months));
                f.properties.TicketAverage = f.properties.VehicleCount === 0
                    ? 0
                    : _.divide(f.properties.Value, f.properties.VehicleCount);
                f.properties.SalesTypeAverages = this._salesFeatures[f.properties.Id].SalesTypeAverages;
                return;
            }

            f.properties.Total = 0;
            f.properties.Value = 0;
            f.properties.VehicleCount = 0;
            f.properties.TicketAverage = 0;
            f.properties.SalesTypeAverages = [];
        });

        const newFeatures = _.cloneDeep(networksFeatures);

        this._networkLayer.setFeatures(newFeatures);
    }

    _removeTags(name, reload = true) {
        const tags = this._networkLayer.options.tag || [];
        this._networkLayer.setOptions({ tag: tags.filter(tag => tag.name !== name) });

        if (this.getControls().switch.onOff.status && reload) {
            this._networkLayer.reload();
        }
    }

    _toggleSales(checked, name, reload = true) {
        if (checked && this._networkLayer.state.visible && this.getControls().switch.onOff.status) {
            this._loadDataForTags();
            this._networkLayer.setOptions({ tag: this._getLayerTag() });

            if (this._networkLayer.visible() && reload) {
                this._networkLayer.reload();
            }
        } else {
            this._removeTags(name, reload);
        }
    }

    _toggleLabel() {
        if (this.getLayer().visible() && this._networkLayer.visible()) {
            this._networkLayer.setOptions({ tag: this._getLayerTag() });
            this._networkLayer.reload();
        }
    }
}

export default ClientSalesAfterSalesControl;



// WEBPACK FOOTER //
// ./src/scripts/controls/AfterSales/ClientSalesAfterSalesControl.js